/**
 * Ready-to-Ship Variant Matching
 *
 * Custom element that subscribes to variantChange events. When the selected
 * MTO variant has a matching RTS variant with inventory, swaps the hidden
 * form input to the RTS variant ID. Updates shipping timeframe labels on
 * the size pills.
 *
 * Uses a custom element (not an IIFE) so that disconnectedCallback can
 * unsubscribe from PubSub when the DOM is replaced during product swaps
 * (HTMLUpdateUtility.viewTransition re-injects scripts, which would
 * duplicate IIFE subscribers without cleanup).
 *
 * The hidden input[name=id] always holds the actual variant to purchase,
 * so both "Add to Cart" and dynamic checkout (Shop Pay) work correctly.
 *
 * NOTE on "effective variant" state divergence:
 * The variantChange PubSub event always carries the MTO variant data
 * (published by product-info.js). This module silently swaps the hidden
 * input to an RTS variant ID when available. Other subscribers (e.g.,
 * one-click-upsell.js) will see MTO variant data in the event, which is
 * correct for their needs (size name, price). If future modules need to
 * know whether the "effective" cart variant is RTS or MTO, they should
 * read the hidden input value directly or check the RTS data map.
 */
class ReadyToShipVariants extends HTMLElement {
  connectedCallback() {
    const rtsDataEl = this.querySelector('[data-rts-variants]');
    if (!rtsDataEl) return;

    try {
      this.rtsData = JSON.parse(rtsDataEl.textContent);
    } catch (e) {
      return;
    }

    this.productInfo = this.closest('product-info');
    if (!this.productInfo) return;

    this.sectionId = this.productInfo.dataset.section;

    // The option name used for sizes (not hardcoded to option1 position).
    this.sizeOptionName = this.dataset.sizeOptionName || 'Size';

    // Build map: size name -> RTS variant (only in-stock variants)
    this.rtsBySize = {};
    this.rtsData.variants.forEach((v) => {
      if (v.available) {
        this.rtsBySize[v.sizeName] = v;
      }
    });

    // Initialize pill labels and initial variant state
    this.updateTimeframeLabels();
    this.handleInitialVariant();

    // Subscribe and store unsubscribe function for cleanup
    this.unsubscribe = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  /**
   * Extract the Size value from a variant object by option name,
   * not by assuming it is always option1.
   */
  getSizeFromVariant(variant) {
    if (!variant) return null;
    const optionsJson = this.dataset.productOptions;
    if (optionsJson) {
      try {
        const options = JSON.parse(optionsJson);
        const target = this.sizeOptionName.toLowerCase();
        const sizeIndex = options.findIndex(
          (name) => name.toLowerCase() === target
        );
        if (sizeIndex >= 0) {
          return variant[`option${sizeIndex + 1}`];
        }
      } catch (e) { /* fall through */ }
    }
    // Fallback: try option1 (most common)
    return variant.option1;
  }

  getRtsVariant(sizeName) {
    return this.rtsBySize[sizeName] || null;
  }

  updateFormVariantId(variantId) {
    this.productInfo
      .querySelectorAll(
        `#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}`
      )
      .forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (input && input.value !== String(variantId)) {
          input.value = variantId;
        }
      });
  }

  updateTimeframeLabels() {
    const pills = this.querySelectorAll('.variant-pill__timeframe');
    pills.forEach((pill) => {
      const sizeName = pill.dataset.sizeName;
      if (this.rtsBySize[sizeName]) {
        pill.textContent = this.rtsData.shippingTimeframe;
        pill.classList.add('variant-pill__timeframe--rts');
      } else {
        pill.textContent = this.rtsData.mtoTimeframe;
        pill.classList.remove('variant-pill__timeframe--rts');
      }
    });
  }

  updateShippingHeader(sizeName) {
    const header = this.querySelector('[data-shipping-message]');
    if (!header) return;
    const rtsVariant = this.getRtsVariant(sizeName);
    const timeframe = rtsVariant
      ? this.rtsData.shippingTimeframe
      : this.rtsData.mtoTimeframe;
    header.textContent = `ships to you in ${timeframe}`;
  }

  handleVariantChange({ data }) {
    const variant = data?.variant;
    if (!variant) return;

    const sizeName = this.getSizeFromVariant(variant);
    if (!sizeName) return;

    const rtsVariant = this.getRtsVariant(sizeName);
    if (rtsVariant) {
      this.updateFormVariantId(rtsVariant.id);
    }
    // If no RTS, the MTO variant ID is already in the input
    // (set by product-info.js updateVariantInputs)

    this.updateShippingHeader(sizeName);

    // Shopify re-renders section HTML after variantChange, resetting pill
    // labels to the server-rendered MTO defaults. Re-apply RTS labels
    // after the DOM update completes.
    setTimeout(() => this.updateTimeframeLabels(), 200);
  }

  handleInitialVariant() {
    const selectedVariantEl = this.querySelector('[data-selected-variant]');
    if (!selectedVariantEl) return;
    try {
      const variant = JSON.parse(selectedVariantEl.textContent);
      if (!variant) return;
      const sizeName = this.getSizeFromVariant(variant);
      if (!sizeName) return;
      const rtsVariant = this.getRtsVariant(sizeName);
      if (rtsVariant) {
        this.updateFormVariantId(rtsVariant.id);
      }
      this.updateShippingHeader(sizeName);
    } catch (e) { /* no-op */ }
  }
}

customElements.define('ready-to-ship-variants', ReadyToShipVariants);
